/**
 * EditTeamModal - Edit existing team via backend-driven form (DRR-061, DRR-055)
 *
 * Uses EntityFormGenerator with mode: 'EDIT' and pre-populated initialValues.
 * Only editable fields are surfaced (name/code/teamType set on create only).
 */

import { useMemo } from 'react';
import { Modal, Paper } from '@mantine/core';
import type { GqlTeam } from '@ferp/workflow-forms';
import { EntityFormGenerator } from '@ferp/workflow-forms';
import type { UIContext } from '@ferp/workflow-forms';

interface EditTeamModalProps {
  team: GqlTeam | null;
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  loading?: boolean;
}

export function EditTeamModal({
  team,
  opened,
  onClose,
  onSubmit,
  loading = false,
}: EditTeamModalProps) {
  const editContext: UIContext = useMemo(
    () => ({
      mode: 'EDIT' as const,
      tenantId: team?.tenantId ?? '',
      safetyLevel: 'STANDARD',
    }),
    [team?.tenantId],
  );

  // Flatten team data into form values for EntityFormGenerator
  const initialValues = useMemo(() => {
    if (!team) return {};
    return {
      name: team.name,
      code: team.code,
      teamType: team.teamType,
      description: team.description ?? '',
      parentTeamId: team.parentTeamId ?? '',
      costCenter: team.costCenter ?? '',
      effectiveDate: team.effectiveDate ?? '',
      budgetAllocated: team.budgetAllocated ?? '',
      tags: team.tags?.join(', ') ?? '',
      teamLeadId: team.teamLeadId ?? '',
      deputyLeadId: team.deputyLeadId ?? '',
      contactEmail: team.contacts?.email ?? '',
      contactChatChannel: team.contacts?.chatChannel ?? '',
      contactWikiUrl: team.contacts?.wikiUrl ?? '',
      contactCalendarUrl: team.contacts?.calendarUrl ?? '',
    };
  }, [team]);

  if (!team) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit Team: ${team.name}`}
      size="lg"
    >
      <Paper p="md">
        <EntityFormGenerator
          subgraph="core"
          entityType="Team"
          context={editContext}
          initialValues={initialValues}
          onSubmit={onSubmit}
          disabled={loading}
          loading={loading}
        />
      </Paper>
    </Modal>
  );
}
